import type {
  LocationQuery,
  NavigationGuard,
  RouteLocationNormalized,
} from "vue-router";
import { useAuth } from "@wovar/common/auth/store.js";

/**
 * Vue router guard, to be used in a `beforeEach`
 *
 * Assumes routes need authentication by default,
 * a route can opt out using meta `allowAnon` boolean propery.
 */
export const authRouteGuard: NavigationGuard = async (
  to: RouteLocationNormalized,
) => {
  if (to.meta.allowAnon) {
    return true;
  }

  const auth = useAuth();
  if (to.path === "/auth/logout") {
    await auth.logoutCallback();
  } else if (hasAuthParams(to.query)) {
    try {
      await auth.loginCallback();
    } catch (e) {
      // Need to implement an error page/view?
      console.error(e);
    } finally {
      // Check if there's a stored redirect URL from before login
      const storedRedirectUrl = sessionStorage.getItem("auth_redirect_url");
      if (storedRedirectUrl) {
        sessionStorage.removeItem("auth_redirect_url");
        return storedRedirectUrl;
      }
      return { name: "home" };
    }
  }
  if (auth.isAuthenticated) {
    return true;
  }

  // Store the current URL before redirecting to login
  sessionStorage.setItem("auth_redirect_url", to.fullPath);
  await auth.loginRedirect();
};

const hasAuthParams = (q: LocationQuery): boolean => {
  return !!((q["code"] || q["error"]) && q["state"]);
};
